<script setup>
// MultiSelect.vue
import { defineModel, computed, ref, watch, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useElementBounding, useWindowSize } from '@vueuse/core';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';

const { options, maxChips, showSearch } = defineProps({
  options: {
    type: Array,
    required: true,
  },
  maxChips: {
    type: Number,
    default: 3,
  },
  showSearch: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();
const selected = defineModel({
  type: [Array, String],
  required: true,
});

const isOpen = ref(false);
const triggerRef = ref(null);
const searchInputRef = ref(null);
const searchQuery = ref('');

const { top, left, width, bottom } = useElementBounding(triggerRef);
const { height: windowHeight } = useWindowSize();

const dropdownStyle = computed(() => {
  const DROPDOWN_HEIGHT = 320;
  const spaceBelow = windowHeight.value - bottom.value;
  const openUp = spaceBelow < DROPDOWN_HEIGHT && top.value > DROPDOWN_HEIGHT;

  return {
    position: 'fixed',
    left: `${left.value}px`,
    minWidth: `${Math.max(width.value, 220)}px`,
    zIndex: 2147483647, // Max int32 - highest possible z-index
    ...(openUp
      ? { bottom: `${windowHeight.value - top.value}px` }
      : { top: `${bottom.value + 4}px` }
    ),
  };
});

const hasItems = computed(() => {
  if (!selected.value) return false;
  if (!Array.isArray(selected.value)) return false;
  if (selected.value.length === 0) return false;

  return true;
});

const selectedIds = computed(() => {
  if (!hasItems.value) return [];
  return selected.value.map(value => value.id);
});

const selectedItems = computed(() => {
  if (!hasItems.value) return [];
  return options.filter(option => selectedIds.value.includes(option.id));
});

const selectedVisibleItems = computed(() => {
  if (!hasItems.value) return [];
  if (selectedItems.value.length === maxChips + 1) return selectedItems.value;
  return selectedItems.value.slice(0, maxChips);
});

const remainingItems = computed(() => {
  if (!hasItems.value) return [];
  if (selectedItems.value.length === maxChips + 1) return [];
  return selectedItems.value.slice(maxChips);
});

const remainingTooltip = computed(() => {
  if (!hasItems.value) return '';
  return remainingItems.value.map(item => item.name).join(', ');
});

// Filtrar opções baseado na pesquisa
const filteredOptions = computed(() => {
  if (!searchQuery.value.trim()) {
    return options;
  }
  
  const query = searchQuery.value.toLowerCase().trim();
  return options.filter(option => 
    option.name.toLowerCase().includes(query)
  );
});

const toggleOption = option => {
  const optionToToggle = {
    id: option.id,
    name: option.name,
  };

  const idToToggle = optionToToggle.id;

  if (!hasItems.value) {
    selected.value = [optionToToggle];
    return;
  }

  if (selectedIds.value.includes(idToToggle)) {
    selected.value = selected.value.filter(value => value.id !== idToToggle);
  } else {
    selected.value = [...selected.value, optionToToggle];
  }
};

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const close = () => {
  isOpen.value = false;
  searchQuery.value = '';
};

// Focar no input de pesquisa quando abrir
watch(isOpen, (newValue) => {
  if (newValue) {
    requestAnimationFrame(() => {
      document.addEventListener('click', handleClickOutside);
      if (showSearch) {
        nextTick(() => {
          searchInputRef.value?.focus();
        });
      }
    });
  } else {
    document.removeEventListener('click', handleClickOutside);
    searchQuery.value = '';
  }
});

// Click outside handler
const handleClickOutside = (event) => {
  if (!isOpen.value) return;
  
  // Acessar o elemento DOM, seja ele nativo ou via $el do componente Vue
  const triggerEl = triggerRef.value?.$el || triggerRef.value;
  const dropdownEl = document.querySelector('[data-multiselect-dropdown]');
  
  // Usar optional chaining para evitar erro caso o elemento não esteja disponível
  if (triggerEl?.contains?.(event.target)) return;
  if (dropdownEl?.contains(event.target)) return;
  
  close();
};

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="relative inline-block">
    <button
      v-if="hasItems"
      ref="triggerRef"
      class="bg-n-alpha-2 py-2 rounded-lg h-8 flex items-center px-0"
      @click="toggle"
    >
      <div
        v-for="item in selectedVisibleItems"
        :key="item.name"
        class="px-3 border-r rtl:border-l rtl:border-r-0 border-n-weak text-n-slate-12 text-sm flex gap-2 items-center max-w-[100px]"
      >
        <Icon v-if="item.icon" :icon="item.icon" class="flex-shrink-0" />
        <span class="truncate">{{ item.name }}</span>
      </div>
      <div
        v-if="remainingItems.length > 0"
        v-tooltip.top="remainingTooltip"
        class="px-3 border-r rtl:border-l rtl:border-r-0 border-n-weak text-n-slate-12 text-sm flex gap-2 items-center max-w-[100px]"
      >
        <span class="truncate">{{
          t('COMBOBOX.MORE', { count: remainingItems.length })
        }}</span>
      </div>
      <div class="flex items-center border-none px-3 gap-2">
        <Icon icon="i-lucide-plus" />
      </div>
    </button>
    <Button v-else ref="triggerRef" sm slate faded @click="toggle">
      <template #icon>
        <Icon icon="i-lucide-plus" class="text-n-slate-11" />
      </template>
      <span class="text-n-slate-11">{{ t('COMBOBOX.PLACEHOLDER') }}</span>
    </Button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        :style="dropdownStyle"
        class="bg-n-solid-2 border border-n-weak rounded-lg shadow-lg multiselect-dropdown"
        data-multiselect-dropdown
      >
        <!-- Campo de pesquisa -->
        <div v-if="showSearch" class="p-2 border-b border-n-weak">
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="w-full px-3 py-1.5 text-sm bg-n-solid-1 border border-n-weak rounded-md text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-blue-9 focus:border-n-blue-9"
            :placeholder="t('COMBOBOX.SEARCH_PLACEHOLDER')"
            @click.stop
          />
        </div>

        <!-- Lista de opções -->
        <div class="max-h-64 overflow-y-auto p-1">
          <template v-if="filteredOptions.length > 0">
            <button
              v-for="option in filteredOptions"
              :key="option.id"
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-3 rounded-md transition-colors cursor-pointer"
              :class="{
                'bg-n-blue-3 text-n-blue-11': selectedIds.includes(option.id),
              }"
              @click.stop="toggleOption(option)"
            >
              <Icon
                v-if="option.icon"
                :icon="option.icon"
                class="flex-shrink-0"
                :class="selectedIds.includes(option.id) ? 'text-n-blue-9' : 'text-n-slate-11'"
              />
              <span class="flex-1 text-left truncate">{{ option.name }}</span>
              <Icon
                v-if="selectedIds.includes(option.id)"
                icon="i-lucide-check"
                class="text-n-blue-9 flex-shrink-0"
              />
            </button>
          </template>
          <div v-else class="px-3 py-4 text-sm text-n-slate-10 text-center">
            {{ t('COMBOBOX.NO_RESULTS') }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>